<template>
  <!-- 会話の吹き出し一覧。CareerAI から切り出した表示専用の葉。
       状態は持たず、押されたものは emit で親へ返す -->
  <div class="chat-messages" ref="container">
    <div class="chat-date">{{ chatDate }}</div>

    <template v-for="(msg, i) in messages">
      <!-- システム通知 -->
      <div
        v-if="msg.role === 'system-notice'"
        :key="'n' + i"
        class="chat-message chat-system"
      >
        <div class="system-bubble">
          <div class="system-content">
            <svg
              class="system-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.6"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
            <div class="system-text">{{ msg.text }}</div>
          </div>
        </div>
      </div>

      <!-- ユーザー / AIバブル -->
      <div
        v-else
        :key="'m' + i"
        class="chat-message"
        :class="msg.role === 'user' ? 'chat-answer' : 'chat-question'"
      >
        <div v-if="msg.role === 'user'" class="answer-bubble">
          <div class="answer-text">{{ msg.text }}</div>
        </div>
        <div
          v-else-if="msg.text"
          class="question-bubble"
          :class="{ 'question-bubble--placeholder': msg.isPlaceholder }"
        >
          <div
            class="question-text"
            v-html="renderMarkdown(msg.text)"
            @click="$emit('link-click', $event)"
          ></div>
        </div>

        <!-- 求人カード -->
        <template
          v-if="msg.role === 'assistant' && msg.jobIds && msg.jobIds.length"
        >
          <div class="job-cards">
            <CareerAiJobCard
              v-for="id in msg.jobIds"
              :key="id"
              :job="getJob(id)"
              :disabled="!!(getJob(id) && getJob(id).disabled)"
              @apply="$emit('apply', $event)"
            />
          </div>
        </template>
      </div>
    </template>

    <!-- 初回サジェスト（チャット欄に表示。入力欄ではなく会話エリア側） -->
    <div
      v-if="showQuickReplies"
      class="quick-reply-container quick-reply-choices"
    >
      <button
        v-for="q in quickReplies"
        :key="q"
        class="quick-reply-button"
        @click="$emit('prefill', q)"
      >
        {{ q }}
      </button>
      <!-- 会話に入らず別の画面へ送る選択肢。チャットを残すため別タブで開く -->
      <a
        v-for="link in quickLinks"
        :key="link.href"
        :href="link.href"
        target="_blank"
        rel="noopener"
        class="quick-reply-button quick-reply-link"
        @click="$emit('link-click', $event)"
      >
        {{ link.label }}
      </a>
    </div>

    <!-- 考え中インジケータ -->
    <div v-if="loading" class="chat-message chat-question">
      <div class="thinking-bubble">
        <i class="el-icon-loading thinking-icon"></i>
        <span class="thinking-text">{{ loadingText }}</span>
      </div>
    </div>

    <!-- 回答フィードバック（わかった/わからない → わからない時はお問い合わせ導線） -->
    <div v-if="feedbackTargetIndex >= 0" class="answer-feedback">
      <template v-if="!feedbackGiven[feedbackTargetIndex]">
        <span class="answer-feedback__label">解決しましたか？</span>
        <div class="answer-feedback__chips">
          <button
            class="feedback-chip"
            @click="
              $emit('feedback', {
                index: feedbackTargetIndex,
                value: 'understood'
              })
            "
          >
            わかった
          </button>
          <button
            class="feedback-chip"
            @click="
              $emit('feedback', {
                index: feedbackTargetIndex,
                value: 'not_understood'
              })
            "
          >
            わからない
          </button>
        </div>
      </template>
      <div
        v-else-if="feedbackGiven[feedbackTargetIndex] === 'understood'"
        class="answer-feedback__thanks"
      >
        お役に立ててよかったです！
      </div>
      <template v-else>
        <span class="answer-feedback__label"
          >お力になれずすみません。<br />よろしければお問い合わせください。</span
        >
        <a
          class="feedback-contact-btn"
          href="/contact"
          target="_blank"
          rel="noopener"
          @click="$emit('contact-click')"
          >お問い合わせはこちら</a
        >
      </template>
    </div>

    <!-- 他のAI機能を選んだとき。全画面へ移ってよいかここで確認する。
         isNudge（数往復後の全画面案内）は再開始しないので「このまま続ける」だけ -->
    <div v-if="pendingService" class="answer-feedback">
      <div class="answer-feedback__chips">
        <button
          class="feedback-chip"
          :disabled="loading"
          @click="$emit('service-confirm')"
        >
          {{ confirmLabels.accept }}
        </button>
        <button
          v-if="pendingService.mode && canStayInChat && !pendingService.isNudge"
          class="feedback-chip"
          @click="$emit('service-stay')"
        >
          {{ confirmLabels.stay }}
        </button>
        <button v-else class="feedback-chip" @click="$emit('service-cancel')">
          {{
            pendingService.isNudge && canStayInChat
              ? confirmLabels.continue
              : confirmLabels.cancel
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

import CareerAiJobCard from '@/components/CareerAIJobCard'
import { formatChatDate, renderMarkdown } from '@/utils/ai/careerAiText'

/* eslint-disable @typescript-eslint/no-explicit-any */
const props = defineProps({
  messages: { type: Array as () => any[], default: () => [] },
  // 求人カードの実体。メッセージは jobIds しか持たない
  jobsCache: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  loadingText: { type: String, default: '考え中…' },
  quickReplies: { type: Array as () => string[], default: () => [] },
  quickLinks: {
    type: Array as () => { label: string; href: string }[],
    default: () => []
  },
  showQuickReplies: { type: Boolean, default: false },
  // 「解決しましたか？」を出す対象index。出さないなら -1
  feedbackTargetIndex: { type: Number, default: -1 },
  feedbackGiven: { type: Object, default: () => ({}) },
  // 選択済みで移動待ちのAI機能。あるとき確認チップを出す
  pendingService: { type: Object, default: null },
  // 確認チップの文言。正本は utils/ai/careerChatMenu.json
  confirmLabels: { type: Object, default: () => ({ accept: '', cancel: '' }) },
  // 本日の上限に達していると送信できないため「この画面で続ける」を出さない
  canStayInChat: { type: Boolean, default: true }
})
defineEmits([
  'apply',
  'prefill',
  'feedback',
  'contact-click',
  'link-click',
  'service-confirm',
  'service-stay',
  'service-cancel'
])

const container = ref<HTMLElement | null>(null)

// 日付はマウント時点で確定。毎描画で new Date() を作らない
const chatDate = computed(() => formatChatDate(new Date()))

const getJob = (id: string): any => (props.jobsCache as any)[id] || null

const scrollToBottom = (): void => {
  nextTick(() => {
    const el = container.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

defineExpose({ scrollToBottom })
</script>

<style
  lang="scss"
  scoped
  src="@/assets/scss/career-ai/career-ai-messages.scss"
></style>
